import { motion } from "framer-motion";
import { Phone, ArrowDown, MapPin } from "lucide-react";
import WheelCanvas from "@/components/WheelCanvas";
import { scrollToId } from "@/lib/scroll";
import { BUSINESS } from "@/data/services";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const HeroSection = () => (
  <section
    id="hero"
    data-testid="hero-section"
    className="relative min-h-screen flex items-center overflow-hidden noise-overlay"
  >
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_40%,rgba(0,240,255,0.08),transparent_55%)]" />
    <div className="absolute right-[-10%] top-1/2 -translate-y-1/2 w-[75vw] h-[75vw] max-w-[900px] max-h-[900px] opacity-70 md:opacity-100">
      <WheelCanvas />
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 w-full pt-28 pb-20">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.15 }}
        className="flex items-center gap-3 mb-8"
      >
        <span className="w-2 h-2 rounded-full bg-volt pulse-dot" />
        <span className="font-mono2 text-[11px] sm:text-xs uppercase tracking-[0.35em] text-volt">
          Mobile Automotive Restoration
        </span>
      </motion.div>

      <h1 data-testid="hero-title" className="font-syne font-extrabold uppercase leading-[0.92] tracking-tight text-5xl sm:text-7xl lg:text-8xl">
        <MaskedLine delay={0.25}>
          <span className="metallic-gold">Elite</span>
        </MaskedLine>
        <MaskedLine delay={0.38}>
          <span className="metallic-text">Rims <span className="text-outline-volt">&amp;</span></span>
        </MaskedLine>
        <MaskedLine delay={0.51}>
          <span className="text-outline">Trims</span>
        </MaskedLine>
      </h1>

      <MaskedLine delay={0.7} className="mt-8 max-w-xl">
        <p data-testid="hero-tagline" className="text-base md:text-lg text-slate-400 leading-relaxed">
          {BUSINESS.tagline}. Damaged rims, peeling steering wheels, worn-out interiors —
          restored like new.
        </p>
      </MaskedLine>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
        className="mt-10 flex flex-wrap items-center gap-4"
      >
        <button
          data-testid="hero-cta-book"
          onClick={() => scrollToId("booking")}
          className="group bg-volt text-carbon font-syne font-bold text-sm uppercase tracking-widest rounded-full px-8 py-4 hover:bg-white hover:text-carbon transition-colors duration-300 glow-volt"
        >
          Book a Service
        </button>
        <a
          data-testid="hero-cta-call"
          href={`tel:${BUSINESS.phone}`}
          className="flex items-center gap-3 border border-edge hover:border-volt/70 text-slate-200 hover:text-volt font-mono2 text-sm tracking-widest rounded-full px-7 py-4 transition-colors duration-300"
        >
          <Phone size={16} /> {BUSINESS.phone}
        </a>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.05 }}
        className="mt-6 flex items-center gap-2.5"
      >
        <MapPin size={14} className="text-volt shrink-0" />
        <span className="font-mono2 text-[11px] sm:text-xs uppercase tracking-[0.25em] text-slate-400">
          Servicing Wanneroo to Serpentine — we come to you
        </span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="mt-14 flex flex-wrap gap-x-12 gap-y-4"
      >
        {[
          ["17+", "Specialist Services"],
          ["04", "Restoration Divisions"],
          ["+GST", "Transparent Pricing"],
        ].map(([num, label]) => (
          <div key={label} className="flex items-baseline gap-3">
            <span className="font-syne font-extrabold text-2xl text-volt">{num}</span>
            <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</span>
          </div>
        ))}
      </motion.div>
    </div>

    <motion.button
      data-testid="hero-scroll-indicator"
      onClick={() => scrollToId("services")}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.6, duration: 1 }}
      className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500 hover:text-volt transition-colors"
      aria-label="Scroll to services"
    >
      <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
        <ArrowDown size={20} />
      </motion.div>
    </motion.button>
  </section>
);

export default HeroSection;
